import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * BannerCarousel — customer-facing hero strip.
 *  - Horizontally snap-scrolling
 *  - Autoplay every 5s, pauses on touch / drag
 *  - Dots indicator
 *  - Click/tap routes via the banner CTA (restaurant / external / search / coupons)
 *
 * `banners` props are admin-curated objects shaped like the backend Banner model.
 */
// ``slideWidth`` lets a theme lay banners out differently per breakpoint (e.g. a CSS variable set by media
// queries). Defaults to the original 85% so every existing caller renders exactly as before.
export default function BannerCarousel({ banners, slideWidth = "85%" }) {
    const scrollRef = useRef(null);
    const [active, setActive] = useState(0);
    const [paused, setPaused] = useState(false);
    const nav = useNavigate();

    const list = (banners || []).filter((b) => b.enabled !== false);

    useEffect(() => {
        if (paused || list.length <= 1) return;
        const id = setInterval(() => {
            const next = (active + 1) % list.length;
            setActive(next);
            const el = scrollRef.current;
            if (!el) return;
            const card = el.children[next];
            if (card) el.scrollTo({ left: card.offsetLeft - 16, behavior: "smooth" });
        }, 5000);
        return () => clearInterval(id);
    }, [active, paused, list.length]);

    const onScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        const x = el.scrollLeft + el.clientWidth / 2;
        let idx = 0;
        for (let i = 0; i < el.children.length; i++) {
            const c = el.children[i];
            if (c.offsetLeft <= x && c.offsetLeft + c.clientWidth >= x) { idx = i; break; }
        }
        setActive(idx);
    };

    const onClick = (b) => {
        if (!b.cta_type || b.cta_type === "NONE") return;
        if (b.cta_type === "RESTAURANT" && b.cta_target) nav(`/restaurant/${b.cta_target}`);
        else if (b.cta_type === "EXTERNAL" && b.cta_target) window.open(b.cta_target, "_blank", "noopener");
        else if (b.cta_type === "COUPON") nav(`/coupons`);
        else if (b.cta_type === "SEARCH" && b.cta_target) nav(`/search?q=${encodeURIComponent(b.cta_target)}`);
    };

    if (list.length === 0) return null;

    return (
        <div className="mt-1" data-testid="banner-carousel"
            onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
            onTouchStart={() => setPaused(true)} onTouchEnd={() => setPaused(false)}>
            <div
                ref={scrollRef}
                onScroll={onScroll}
                className="flex gap-3 px-4 overflow-x-auto no-scrollbar snap-x snap-mandatory"
            >
                {list.map((b) => (
                    <button
                        key={b.id || b.title}
                        data-testid={`banner-${b.id || b.title}`}
                        onClick={() => onClick(b)}
                        className="snap-center shrink-0 rounded-2xl overflow-hidden relative shadow-sm active:scale-[0.99] transition"
                        style={{ background: b.background_color || "#5B21B6", color: b.text_color || "#fff", width: slideWidth, aspectRatio: "16 / 7.5" }}
                    >
                        {/* iter134f — full-image mode: render the
                            uploaded banner edge-to-edge, no text
                            overlay. Auto-generated layout kicks in
                            below when the merchant left ``image_only``
                            off (or unset for legacy rows). */}
                        {b.image_only && b.image_url ? (
                            <img
                                src={b.image_url}
                                alt={b.title || "Banner"}
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                                onError={(e) => { e.target.style.display = "none"; }}
                            />
                        ) : (
                            <>
                                <div className="flex items-center h-full px-5 py-4 text-left">
                                    <div className="flex-1 z-10 max-w-[64%]">
                                        <p className="font-bold text-lg leading-tight line-clamp-2">{b.title}</p>
                                        {b.subtitle && <p className="text-xs opacity-90 mt-1 line-clamp-1">{b.subtitle}</p>}
                                        {b.cta_label && b.cta_type !== "NONE" && (
                                            <span className="inline-block mt-2 bg-white/95 text-zinc-900 rounded-full text-[11px] font-bold px-3 py-1.5">
                                                {b.cta_label}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                {b.image_url && (
                                    <img src={b.image_url} alt=""
                                        className="absolute right-0 top-0 h-full w-[45%] object-cover opacity-90"
                                        onError={(e) => { e.target.style.display = "none"; }} />
                                )}
                            </>
                        )}
                    </button>
                ))}
            </div>
            {list.length > 1 && (
                <div className="flex items-center justify-center gap-1.5 mt-2">
                    {list.map((b, i) => (
                        <span key={b.id || `${b.title}-${i}`} className={`h-1.5 rounded-full transition-all ${i === active ? "w-5 bg-primary" : "w-1.5 bg-muted"}`} />
                    ))}
                </div>
            )}
        </div>
    );
}
